import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { GAME_RULES, encodeForageFrame } from '@continuum/show-core';
import type { ForageTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';

interface Mushroom {
  id: number;
  /** Where it grows, as fractions of the board. */
  x: number;
  y: number;
  picked: boolean;
}

/** The board's height against its width, near enough, for keeping mushrooms apart. */
const ASPECT = 0.8;

/** Spread the mushrooms out, so no two can be picked with one tap. */
function grow(count: number): Mushroom[] {
  const out: Mushroom[] = [];
  let guard = 0;
  while (out.length < count && guard < 500) {
    guard += 1;
    const x = 0.1 + Math.random() * 0.8;
    const y = 0.14 + Math.random() * 0.72;
    if (out.some((m) => Math.hypot(m.x - x, (m.y - y) * ASPECT) < 0.22)) continue;
    out.push({ id: out.length, x, y, picked: false });
  }
  return out;
}

/**
 * BIOTECHNICIAN, 2617 — under the sand, in the dark. The finger carries a candle: slide it round
 * the board and whatever grows near it shows; tap a mushroom you can see to pick it. A tap in the
 * dark only moves the candle, so there is nothing to get wrong and nothing to hurry.
 *
 * It replaced "Share the seeds" (cut bars into four), which nobody at the test evening understood.
 */
export function Forage({ task, onProgress, onWin, onFrame, paused = false, s }: GameProps<ForageTask>) {
  const { lightRadius, reach } = GAME_RULES.forage;
  const [mushrooms, setMushrooms] = useState<Mushroom[]>(() => grow(task.count));
  const [light, setLight] = useState<{ x: number; y: number } | null>(null);
  const lightRef = useRef(light);
  lightRef.current = light;
  const dragging = useRef<number | null>(null);
  const won = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame });
  cbs.current = { onProgress, onWin, onFrame };
  const hold = useRef(paused);
  hold.current = paused;

  const found = mushrooms.filter((m) => m.picked).length;

  // Where the candle is and how many are picked, for anyone watching.
  useEffect(() => {
    if (light) cbs.current.onFrame(encodeForageFrame(light.x, light.y, found));
  }, [light, found]);

  /** A point on the board, as fractions, and the board's size in pixels. */
  const at = (e: ReactPointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)),
      y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)),
      w: box.width,
      h: box.height,
    };
  };
  const px = (a: { x: number; y: number }, b: { x: number; y: number }, w: number, h: number) =>
    Math.hypot((a.x - b.x) * w, (a.y - b.y) * h);

  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (hold.current) return;
    const p = at(e);
    dragging.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* the finger already left; the candle still moves */
    }
    // A tap on a mushroom the candle already shows picks it; anywhere else the candle goes there.
    const was = lightRef.current;
    const seen = (m: Mushroom) => was !== null && px(m, was, p.w, p.h) <= lightRadius * 0.9 * p.w;
    const hit = mushrooms.find((m) => !m.picked && seen(m) && px(m, p, p.w, p.h) <= reach * p.w);
    setLight({ x: p.x, y: p.y });
    if (!hit) return;
    const next = mushrooms.map((m) => (m.id === hit.id ? { ...m, picked: true } : m));
    setMushrooms(next);
    const total = next.filter((m) => m.picked).length;
    cbs.current.onProgress(Math.round((total / task.count) * 100));
    navigator.vibrate?.(15);
    if (total >= task.count && !won.current) {
      won.current = true;
      cbs.current.onWin();
    }
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (hold.current || dragging.current !== e.pointerId) return;
    const p = at(e);
    setLight({ x: p.x, y: p.y });
  };
  const up = () => {
    dragging.current = null;
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ marginBottom: 6, fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: '#e0a860' }}>
        {s.forageFound(found, task.count)}
      </div>
      <div
        data-nodrag
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        style={{
          position: 'relative',
          width: '100%',
          height: 280,
          borderRadius: 14,
          border: '1px solid rgba(224,138,60,.35)',
          // A tunnel under the sand: earth, a little lighter at the floor.
          background: 'linear-gradient(180deg,#070503 0%,#0d0905 70%,#150e07 100%)',
          overflow: 'hidden',
          touchAction: 'none',
          cursor: 'pointer',
        }}
      >
        {/* The candle's glow. */}
        {light && (
          <div
            style={{
              position: 'absolute',
              left: `${light.x * 100}%`,
              top: `${light.y * 100}%`,
              width: `${lightRadius * 200}%`,
              aspectRatio: '1',
              transform: 'translate(-50%,-50%)',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,196,120,.34) 0%, rgba(224,138,60,.16) 45%, rgba(224,138,60,0) 72%)',
              pointerEvents: 'none',
            }}
          />
        )}

        {mushrooms.map((m) => {
          // Seen by candlelight: full near the flame, fading out towards the edge of its glow.
          const box = { w: 1, h: ASPECT };
          const d = light ? Math.hypot((m.x - light.x) * box.w, (m.y - light.y) * box.h) : 9;
          const shown = m.picked ? 0 : Math.min(1, Math.max(0, (lightRadius - d) / (lightRadius * 0.45)));
          return (
            <div
              key={m.id}
              style={{
                position: 'absolute',
                left: `${m.x * 100}%`,
                top: `${m.y * 100}%`,
                width: 38,
                height: 38,
                transform: `translate(-50%,-50%) scale(${m.picked ? 1.4 : 1})`,
                opacity: shown,
                transition: 'opacity .25s ease, transform .3s ease',
                pointerEvents: 'none',
              }}
            >
              <MushroomSprite />
            </div>
          );
        })}

        {/* The candle itself, where the finger is. */}
        {light && (
          <div
            style={{
              position: 'absolute',
              left: `${light.x * 100}%`,
              top: `${light.y * 100}%`,
              width: 10,
              height: 14,
              transform: 'translate(-50%,-50%)',
              borderRadius: '50% 50% 45% 45%',
              background: 'radial-gradient(circle at 50% 70%, #fff4d6 0%, #ffc46b 55%, rgba(255,160,60,0) 100%)',
              pointerEvents: 'none',
            }}
          />
        )}

      </div>
      <div style={{ marginTop: 8, textAlign: 'center', fontFamily: mono, fontSize: 12, letterSpacing: 1, color: '#9db3c2' }}>
        {s.forageHow}
      </div>
    </div>
  );
}

/** A small mushroom: a round cap, a pale stem. Warm, so it reads in candlelight. */
export function MushroomSprite() {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <path d="M17 22 Q16 31 14 34 L26 34 Q24 31 23 22 Z" fill="#efe0c2" />
      <path d="M5 23 Q6 8 20 7 Q34 8 35 23 Q20 27 5 23 Z" fill="#c8642e" />
      <circle cx="14" cy="15" r="2.2" fill="#f3d9b0" />
      <circle cx="24" cy="12" r="1.8" fill="#f3d9b0" />
      <circle cx="28" cy="18" r="1.6" fill="#f3d9b0" />
    </svg>
  );
}
